/**
 * Utility functions for date handling with Eastern Time (ET) timezone
 */

/**
 * Creates a Date object for a specific date and time in Eastern Time (ET)
 * Automatically handles EST (UTC-5) and EDT (UTC-4) based on the date
 * 
 * @param year - Year (e.g., 2026)
 * @param month - Month (1-12, where 1 = January)
 * @param day - Day of month (1-31)
 * @param hour - Hour in 24-hour format (0-23)
 * @param minute - Minute (0-59), defaults to 0
 * @param second - Second (0-59), defaults to 0
 * @returns Date object representing the ET time converted to UTC
 * 
 * @example
 * // January 5, 2026 at 9:00 AM ET
 * const launchDate = createDateInET(2026, 1, 5, 9);
 */
export function createDateInET(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number = 0,
  second: number = 0,
): Date {
  // Determine if the date is in Daylight Saving Time (EDT) or Standard Time (EST)
  const isDST = isDaylightSavingTime(year, month, day);
  const offsetHours = isDST ? 4 : 5; // EDT is UTC-4, EST is UTC-5
  
  // Create UTC date by adding the offset hours
  // This ensures the date represents the correct ET time regardless of server location
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour + offsetHours, minute, second));
  
  return utcDate;
}

/**
 * Determines if a date is in Daylight Saving Time for Eastern Time
 * DST in the US typically runs from 2nd Sunday in March to 1st Sunday in November
 * 
 * @param year - Year
 * @param month - Month (1-12)
 * @param day - Day of month
 * @returns true if date is in DST (EDT), false if in EST
 */
function isDaylightSavingTime(year: number, month: number, day: number): boolean {
  // DST in US typically starts: 2nd Sunday in March
  // DST typically ends: 1st Sunday in November
  
  // If month is before March or after November, it's EST
  if (month < 3 || month > 11) {
    return false;
  }
  
  // If month is April through October, it's definitely DST
  if (month > 3 && month < 11) {
    return true;
  }
  
  // For March: check if on or after 2nd Sunday
  if (month === 3) {
    const secondSunday = getNthSunday(year, 3, 2);
    return day >= secondSunday;
  }
  
  // For November: check if before 1st Sunday
  if (month === 11) {
    const firstSunday = getNthSunday(year, 11, 1);
    return day < firstSunday;
  }
  
  return false;
}

/**
 * Gets the day of month for the nth Sunday in a given month
 * 
 * @param year - Year
 * @param month - Month (1-12)
 * @param n - Which Sunday (1 = first, 2 = second, etc.)
 * @returns Day of month for the nth Sunday
 */
function getNthSunday(year: number, month: number, n: number): number {
  // Find the first day of the month
  const firstDay = new Date(year, month - 1, 1);
  const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate how many days until the first Sunday
  const daysUntilFirstSunday = (7 - firstDayOfWeek) % 7;
  
  // Calculate the nth Sunday
  const nthSunday = 1 + daysUntilFirstSunday + (n - 1) * 7;
  
  return nthSunday;
}

